import { useEffect, useState } from 'react';
import { useSwrTick } from '@/lib/swrRefresh';
import axios from 'axios';
import { ShieldAlert, X } from 'lucide-react';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * IncomeConcentrationBanner — Round 86 (Feb 2026).
 *
 * Standalone self-fetching version of the concentration-risk warning
 * that used to live inside `PortfolioView`. Extracted so it can be
 * mounted alongside the other summary cards (under Cash Balance) per
 * user's preferred layout.
 *
 * Renders nothing when:
 *   • The user has no positions, OR
 *   • No single ticker exceeds the configured concentration threshold
 *     (server returns `max_concentration: null` for balanced portfolios).
 *
 * Self-listens for `portfolio-changed` so adding/removing positions
 * recomputes the warning without a page reload.
 */
export default function IncomeConcentrationBanner({ onStockClick, refreshKey, portfolioId, riskData: providedRiskData, hideWhenContrastShowsBanners = false }) {
  const [riskData, setRiskData] = useState(providedRiskData || null);
  const [tick, setTick] = useState(0);
  const [contrastHasPortfolios, setContrastHasPortfolios] = useState(false);
  const scopeParams = usePortfolioScopeParams();
  // Round 93o — when the parent passes an explicit `portfolioId`,
  // use that (so the Income by Portfolio card can mount one banner
  // PER portfolio section). Otherwise fall back to the user's
  // active-portfolio scope param (legacy free-tier behavior).
  const effectiveScope = portfolioId !== undefined
    ? (portfolioId ? { portfolio_id: portfolioId } : {})
    : scopeParams;
  const scopeKey = portfolioId !== undefined
    ? (portfolioId || 'all')
    : (scopeParams.portfolio_id || 'all');

  const storageKey = `dividrip:concentration-dismissed:${scopeKey}`;
  const readDismissed = () => { try { return localStorage.getItem(storageKey) || null; } catch { return null; } };
  const [dismissedSig, setDismissedSig] = useState(readDismissed);
  useEffect(() => { setDismissedSig(readDismissed()); }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const bump = () => setTick((n) => n + 1);
    window.addEventListener('portfolio-changed', bump);
    return () => window.removeEventListener('portfolio-changed', bump);
  }, []);

  // Round 93o — global mount: detect whether per-portfolio banners
  // are rendering downstream (Plus users with named portfolios) so
  // we don't double-show a concentration warning.
  useEffect(() => {
    if (!hideWhenContrastShowsBanners) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/portfolios/contrast`, { withCredentials: true })
      .then((res) => {
        if (!cancelled) {
          const list = Array.isArray(res.data?.portfolios) ? res.data.portfolios : [];
          setContrastHasPortfolios(list.length > 0);
        }
      })
      .catch(() => { if (!cancelled) setContrastHasPortfolios(false); });
    return () => { cancelled = true; };
  }, [hideWhenContrastShowsBanners, tick, refreshKey]);

  const swrTick = useSwrTick('/portfolio/risk-score');
  useEffect(() => {
    // If the parent already provided riskData (e.g. from the
    // /portfolios/contrast payload), don't re-fetch.
    if (providedRiskData) {
      setRiskData(providedRiskData);
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`${API}/portfolio/risk-score`, { params: effectiveScope, withCredentials: true })
      .then((res) => { if (!cancelled) setRiskData(res.data); })
      .catch(() => { if (!cancelled) setRiskData(null); });
    return () => { cancelled = true; };
  }, [tick, refreshKey, scopeKey, providedRiskData, swrTick]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!riskData?.max_concentration) return null;
  // Round 93o — hide the global mount when the Income by Portfolio
  // card is rendering its own per-portfolio banners.
  if (hideWhenContrastShowsBanners && contrastHasPortfolios) return null;

  const threshold = riskData.concentration_threshold_pct ?? riskData.threshold_pct ?? 25;
  const over = Array.isArray(riskData.over_threshold) && riskData.over_threshold.length > 0
    ? riskData.over_threshold
    : [riskData.max_concentration];
  const total = Number(riskData.total_annual_income) || 0;
  // Oct 2026 — dismiss persists per scope until the picture changes:
  // the signature is the over-threshold set with each % rounded to 5.
  const signature = over.map((o) => `${o.ticker}:${Math.round(o.pct / 5) * 5}`).join('|');
  if (dismissedSig === signature) return null;
  const dismiss = () => {
    try { localStorage.setItem(storageKey, signature); } catch { /* ignore */ }
    setDismissedSig(signature);
  };
  const fmtMoney = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`;
  const tickerBtn = (o) => (
    <button
      key={o.ticker}
      type="button"
      onClick={() => onStockClick?.(o.ticker)}
      className="font-mono font-bold underline underline-offset-2 hover:opacity-80"
      data-testid={`concentration-banner-ticker-${o.ticker}`}
    >
      {o.ticker}
    </button>
  );

  return (
    <div
      className="relative flex items-start gap-3 rounded-lg border border-rose-300/70 dark:border-rose-700/60 bg-rose-50 dark:bg-rose-950/30 px-4 py-3 pr-12 mb-4"
      data-testid="portfolio-concentration-banner"
      role="alert"
    >
      <ShieldAlert className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-rose-800 dark:text-rose-200">
          Income concentration risk
        </p>
        <p className="text-xs text-rose-700/90 dark:text-rose-300/90 mt-0.5 leading-snug" data-testid="concentration-banner-text">
          {over.length === 1 ? (
            <>
              <span className="font-mono font-semibold" data-testid="concentration-banner-pct">{Number(over[0].pct).toFixed(1)}%</span>
              {' of your projected annual dividend income comes from a single ticker — '}
              {tickerBtn(over[0])}
              {`. Consider diversifying so no single position exceeds ${threshold}%.`}
            </>
          ) : (
            <>
              {`${over.length} tickers each supply more than ${threshold}% of your projected annual dividend income: `}
              {over.map((o, i) => (
                <span key={o.ticker}>
                  {i > 0 && (i === over.length - 1 ? ' and ' : ', ')}
                  {tickerBtn(o)}{' '}
                  <span className="font-mono font-semibold" data-testid={`concentration-banner-pct-${o.ticker}`}>{Number(o.pct).toFixed(1)}%</span>
                </span>
              ))}
              {`. Consider diversifying so no single position exceeds ${threshold}%.`}
            </>
          )}
        </p>
        {total > 0 && over[0]?.annual_income != null && (
          <p className="text-[11px] text-rose-700/70 dark:text-rose-300/70 mt-1 font-mono" data-testid="concentration-banner-basis">
            Basis: latest payment × payments per year ·{' '}
            {over.map((o) => `${o.ticker} ${fmtMoney(o.annual_income)}/yr`).join(' · ')} of {fmtMoney(total)}/yr total
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss income concentration warning"
        title="Dismiss — comes back if the concentration picture changes"
        className="absolute right-2.5 top-2.5 inline-flex items-center justify-center w-7 h-7 rounded-md border border-rose-300/80 dark:border-rose-700/70 text-rose-700 dark:text-rose-300 hover:bg-rose-600 hover:text-white hover:border-rose-600 transition-colors"
        data-testid="concentration-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}
